import type { CloudAPI } from '@yandex-360/telemost-sdk/cloud-api';
import { all, call, put, SagaGenerator, select } from 'typed-redux-saga';
import { v4 as uuid } from 'uuid';

import {
    getAppVersionSelector,
    selectIsGoloomRc,
    selectTelemostHost,
} from '../../../../../shared/features/config/selectors';
import { ACTION, FEATURE } from '../../../../../shared/helpers/logger-enums';
import { createLogger } from '../../../../../shared/helpers/loggerFacade';
import {
    retryWithTimeout,
    RetryWithTimeoutError,
    RetryWithTimeoutParams,
} from '../../../../../shared/helpers/redux-saga';
import { cloudApi, IDEMPOTENCY_KEY_HEADER } from '../../../../../shared/helpers/transport';
import { getConferenceQueryString, resolveConferenceUri } from '../../../helpers/conference-config';
import { handleFetchError } from '../../../helpers/handlers-sagas';
import { ConferenceConfig } from '../../../types';
import { setRoomConferenceConfig } from '../slice';
import { fetchMeSaga } from './me';

const FETCH_CONFIG_MAX_ATTEMPTS = 3;

const logger = createLogger('goloom:conference-config');

export function* updateConferenceConfig(
    roomId: string,
    searchString?: string,
    displayName?: string
): SagaGenerator<ConferenceConfig> {
    try {
        const telemostHost = yield* select(selectTelemostHost);
        const isGoloomRc = yield* select(selectIsGoloomRc);
        const appVersion = yield* select(getAppVersionSelector);

        const queryString = getConferenceQueryString(searchString);

        const connectRequest: CloudAPI.ConnectToConference.Request = {
            roomId,
            telemostHost,
            displayName,
            isGoloomRc,
            appVersion,

            searchString: queryString,
            enableGoloom: true,
            additionalHeaders: { [IDEMPOTENCY_KEY_HEADER]: uuid() },
        };

        logger.featureEvent({
            feature: FEATURE.CONNECTION,
            action: ACTION.CONNECT_CONFERENCE,
            options: {
                additional: {
                    roomId,
                },
            },
        });

        const { conference, me } = yield* all({
            conference: call(
                [cloudApi, retryWithTimeout],
                {
                    maxTries: FETCH_CONFIG_MAX_ATTEMPTS,
                    onAttemptError: function* (error, attempt) {
                        logger.warn(error, {
                            method: 'updateConferenceConfig',
                            additional: {
                                retryN: attempt,
                                reason: error instanceof Error ? error.message : undefined,
                            },
                        });
                        logger.featureEvent({
                            feature: FEATURE.CONNECTION,
                            action: ACTION.CONNECT_CONFERENCE,
                            isSuccess: false,
                            options: {
                                additional: {
                                    roomId,
                                    retryN: attempt,
                                    reason: error instanceof Error ? error.message : undefined,
                                },
                            },
                        });
                        return yield* call(handleFetchError, error);
                    },
                } as RetryWithTimeoutParams<typeof cloudApi.connectToConference>,
                cloudApi.connectToConference,
                connectRequest
            ) as SagaGenerator<CloudAPI.ConnectToConference.Response | undefined>,
            me: displayName ? call(() => undefined) : call(fetchMeSaga),
        });

        if (!conference) {
            throw Error('Did not receive conference config');
        }

        const conferenceUri = conference.uri ? conference.uri : `https://${telemostHost}/j/${roomId}`;

        // TODO: кажется (а Кирилл говорит, что не кажется) display_name в конфиге - пережиток MVP1. Нужно убрать
        const config: ConferenceConfig = {
            ...conference,
            resolved_uri: resolveConferenceUri(conferenceUri, searchString),
            ...(me?.displayName ? { display_name: me.displayName } : {}),
        };

        yield* put(setRoomConferenceConfig(config));

        logger.featureEvent({
            feature: FEATURE.CONNECTION,
            action: ACTION.CONNECT_CONFERENCE,
            isSuccess: true,
            options: {
                additional: {
                    roomId,
                },
            },
        });

        return config;
    } catch (error) {
        logger.featureEvent({
            feature: FEATURE.CONNECTION,
            action: ACTION.CONNECT_CONFERENCE,
            isSuccess: false,
            options: {
                additional: {
                    roomId,
                    reason: error instanceof Error ? error.message : undefined,
                },
            },
        });

        if (error instanceof RetryWithTimeoutError) {
            throw RetryWithTimeoutError.enhance(error, {
                additional: {
                    // попытки считаем с 0, потому вычитаем
                    retryN: FETCH_CONFIG_MAX_ATTEMPTS - 1,
                    reason: error.message,
                },
            });
        }

        throw error;
    }
}
